import { Injectable } from '@angular/core';
import { AngularFirestore, CollectionReference, Query } from '@angular/fire/firestore';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {

  constructor(
    private afs: AngularFirestore
  ) { }

  getById(collection: string, id: string) {
    return this.afs.collection(collection).doc(id).snapshotChanges();
  }

  getByIdOnce(collection: string, id: string) {
    return this.afs.collection(collection).doc(id).snapshotChanges().pipe(take(1));
  }

  getByIdValueChangesOnce(collection: string, id: string) {
    return this.afs.collection(collection).doc(id).valueChanges().pipe(take(1));
  }

  getValueChangesById(collection: string, id: string) {
    return this.afs.collection(collection).doc(id).valueChanges()
  }

  getAll(collection: string, orderBy: string, orderDirection:  'desc' | 'asc') {
    return this.afs.collection(collection, ref => {
      if (orderBy) {
        return ref.orderBy(orderBy, orderDirection || "desc");
      }
      return ref;
    }).snapshotChanges();
  }

  getAllOnce(collection: string, orderBy: string, orderDirection:  'desc' | 'asc') {
    return this.afs.collection(collection, ref => {
      if (orderBy) {
        return ref.orderBy(orderBy, orderDirection || "desc");
      }
      return ref;
    }).snapshotChanges().pipe(take(1));
  }

  getAllValueChangesOnce(collection: string, queryArray: any[], orderBy: string, orderDirection:  'desc' | 'asc') {
    return this.afs.collection(collection, ref => {
      let query: Query = ref;
      queryArray.forEach((singleQuery) => {
        query = query.where(singleQuery.field, singleQuery.operator, singleQuery.value);
      });

      if (orderBy) {
        query = query.orderBy(orderBy, orderDirection || "desc");
      }
      return query;
    }).valueChanges().pipe(take(1));
  }

  /**
   * Get all by id
   * Requires an array of search objects
   * let queryArray = [];
        const query = {
          field: 'uid',
          operator: '==',
          value: user.uid
        }
   *
   * @param collection
   * @param query
   * @param orderBy
   * @param direction - 'desc' or 'asc' - null = 'desc'
   * @returns
   */
  getAllBy(collection: string, queryArray: any[], orderBy: string, direction: 'asc' | 'desc') {
    return this.afs.collection(collection, ref => {
      let query: Query = ref;
      queryArray.forEach((singleQuery) => {
        query = query.where(singleQuery.field, singleQuery.operator, singleQuery.value);
      });
      if (orderBy) {
        query = query.orderBy(orderBy, direction);
      }
      return query;
     }).snapshotChanges();
  }

  /**
   * Get all once by id
   * Requires an array of search objects
   * let queryArray = [];
        const query = {
          field: 'uid',
          operator: '==',
          value: user.uid
        }
   *
   * @param collection
   * @param query
   * @param orderBy
   * @param direction - 'desc' or 'asc' - null = 'desc'
   * @returns
   */
  getAllOnceBy(collection: string, queryArray: any[], orderBy: string, direction: 'asc' | 'desc') {
    return this.afs.collection(collection, ref => {
      let query: Query = ref;
      queryArray.forEach((singleQuery) => {
        query = query.where(singleQuery.field, singleQuery.operator, singleQuery.value);
      });
      if (orderBy) {
        query = query.orderBy(orderBy, direction);
      }
      return query;
     }).snapshotChanges().pipe(take(1));
  }

  create(collection: string, data: any) {
    return this.afs.collection(collection).add(data);
  }

  /**
   * Update an entry
   *
   * @param collection
   * @param id
   * @param data
   * @returns
   */
  update(collection: string, id: string, data: any) {
    return this.afs.collection(collection).doc(id).update(data);
  }

  delete(collection: string, id: string) {
    return this.afs.collection(collection).doc(id).delete();
  }
}
